// Admin scheduler copy — single source of truth, transcribed verbatim from the
// approved mockup (temp-data/admin-scheduler-implementation/Admin Scheduler
// Mockup.html). Read-verbatim scripts are compliance-approved: DO NOT EDIT
// without compliance sign-off. The CMS model disclosure inside
// COPY.s4.confirmation must never be modified.

import { FORMER_ICHRA_MESSAGE } from '~/util/former-ichra'

// Product keys are persisted in SignedSoaForm.products / product_answers and
// synced to Salesforce — never rename a value (labels/descs here are the
// admin-facing wording from the mockup, distinct from the client wizard's).
export const ADMIN_PRODUCTS = [
    { value: 'mapd', label: 'Medicare Advantage (Part C)', desc: 'One plan that combines hospital, medical, and usually drug coverage' },
    { value: 'pdp', label: 'Prescription Drug Plan (Part D)', desc: 'A separate plan that helps pay for prescriptions' },
    { value: 'supp', label: 'Medicare Supplement (Medigap)', desc: 'Works with Original Medicare to help pay deductibles and copays' },
    { value: 'dental_vision_hearing', label: 'Dental / Vision / Hearing', desc: 'Covers routine dental, vision, and hearing care' },
]

// Admin scheduler shows exactly these three (no UNSURE), per the ticket.
export const MEDICARE_PARTS_OPTIONS = [
    { value: 'NONE', text: 'None' },
    { value: 'A', text: 'Part A Only' },
    { value: 'AB', text: 'Parts A and B' },
]

export const COPY = {
    page: {
        eyebrow: 'Internal · Scheduling',
        // title/lede removed: pages/schedule/index.vue already renders an h2
        // ("… Schedule Your Consultation") directly above this component, so the
        // agent saw two stacked headings saying the same thing.
    },
    s1: {
        heading: 'Client status & referral',
        sub: 'Route the call before collecting details.',
        workedBefore: 'Have they worked with us before?',
        pillReturning: 'Yes, look up account',
        pillNew: 'No, new client',
        searchLabel: 'Search CRM',
        searchPlaceholder: "Start typing the client's name…",
        recents: 'Recent clients',
        noMatchesClient: 'No matches found. Check the spelling, or switch to “No, new client”.',
        showMore: n => `Show more (${n} more)`,
        referralSource: 'Referral source',
        employerGroup: 'Employer group',
        financialAdvisorGroup: 'Financial advisor group',
        dpcGroup: 'Direct primary care',
        // Paying-source gate: only fullserve clients can book a consultation.
        confirmPayingHint: 'Confirm a paying referral source above to continue.',
        notPayingHeading: 'Not eligible for a scheduled consultation',
        notPayingBody: "This client isn't from a paying referral source, so we can't book a phone or video consultation. Paying sources include a participating employer group, a financial advisor, a direct primary care practice, or Doctor's Choice Internal.",
        // #679 J-rail: aep copy variant for the SAME blocked card above (never
        // names the specific group — mockup panel J).
        notPayingHeadingAep: 'Not eligible',
        notPayingBodyAep: 'This employer does not participate. Consultations and the AEP survey are both unavailable for new clients from this group.',
        notPayingNoteAep: 'Refer the caller to 1-800-MEDICARE or Medicare.gov. Nothing is scheduled and no email is sent.',
        editHeading: 'On file — correct anything that changed',
        identityLocked: 'Name, date of birth, and email identify the account — edit those in the full profile.',
        consultTypeLabel: 'Consultation type',
        pillNewToMedicare: 'New to Medicare',
        pillPlanReview: 'AEP plan review',
        badgePaying: 'Paying partner',
        // CONTROLLER RULING (#679 review, Finding 2): the brief's binding copy
        // — panel K's badge reads "Enrolled" — applies to the RETURNING
        // survey-only client the mockup actually depicts. A NEW client
        // resolved to survey-only via referral (DPC) is a case the mockup
        // never covered, so it keeps "Survey only" rather than misapplying
        // "Enrolled" to someone who was never actually enrolled with us.
        // ClientStatusCard picks between the two by target.returning.
        badgeEnrolled: 'Enrolled',
        badgeSurveyOnly: 'Survey only',
        // Former Benefit Bay (ICHRA) client, closed 2026-10-01. Owner-supplied
        // copy shared with the client wizard and the profile alert
        // (util/former-ichra.js); replaces the not-paying card's text for them.
        formerIchraBody: FORMER_ICHRA_MESSAGE,
        // #727: Other-source clients in an open state (FL/RI).
        clientState: "Client's state",
        clientStateHint: names => `${names} residents can be booked without a paying referral source.`,
        clientStateOther: 'Other',
        badgeOpenState: codes => `${codes.join(' / ')} resident`,
        notPayingOtherHint: names => `Clients who live in ${names} can be booked directly.`,
        whoReferred: 'Who referred them?',
        refClient: 'Current client',
        refClientSub: 'Link to their account',
        refEmployer: 'Employer',
        refEmployerSub: 'Sales may follow up',
        refOther: 'Other',
        refOtherSub: 'Search, event, etc.',
        refClientSearch: 'Search existing clients',
        refClientName: "Client's name",
        refLinked: name => `✓ Linked to ${name}`,
        refUnlink: 'Unlink',
        refEmployerName: 'Employer name',
        refEmployerContact: 'HR / benefits contact',
        refSalesFlag: 'Flag for sales team follow-up',
        refSalesFlagSub: "Auto-checked when the employer isn't a participating group.",
        refOtherDetails: 'Details',
        refOtherPlaceholder: "e.g. Google search, seminar, doctor's office",
    },
    // The form kept in the tab (util/scheduler-draft.js, 2026-10-29).
    draft: {
        restored: t => `Picked up where you left off: this form was kept in this tab at ${t}.`,
        slotDropped: 'The time you picked was let go when the page closed, so pick it again.',
        startOver: 'Start over',
        leaveTitle: 'Leave the scheduler?',
        leaveBody: 'What you have entered stays in this tab, and coming back to this page picks it up again.',
        leaveOk: 'Leave',
        leaveCancel: 'Stay',
    },
    upcoming: {
        heading: 'Already has an upcoming consultation',
        when: w => `Booked for ${w}.`,
        note: 'Booking again creates a second appointment. Reschedule the existing one if the time needs to change.',
        manage: 'View / reschedule →',
    },
    s2: {
        heading: 'Client information',
        // #727: the state comes from step 1 for an Other-source client.
        stateLockedNote: 'Set from step 1. Change it there.',
        identity: 'Identity',
        contact: 'Contact',
        medicare: 'Medicare situation',
        special: 'Special circumstances',
        firstName: 'First name',
        middle: 'Middle',
        lastName: 'Last name',
        dob: 'Date of birth',
        gender: 'Gender',
        groupRelationship: 'Group relationship',
        emailLabel: 'Email',
        phoneLabel: 'Phone',
        consentClient: "Client agreed to receive texts from Doctor's Choice at this number.",
        consentAttendee: "Attendee agreed to receive texts from Doctor's Choice at this number.",
        zipLabel: 'ZIP (living / retiring in)',
        coverageLabel: 'Coverage needed by',
        // Returning clients only. The date on file is deliberately not
        // prefilled into the input (see logic.js fillFromRecord), so this
        // read-only line is how the admin can still see what the account says
        // — and what leaving the box empty will leave it as.
        coverageOnFile: d => `On file: ${d} — leave blank to keep it`,
        partsLabel: 'Current Medicare parts',
        countyCrossWarn: 'This ZIP crosses county lines. Confirm the county:',
        confirmCounty: 'Confirm county',
        hsaQ: 'Contributing to a Health Savings Account (HSA)?',
        hsaTip: 'A health savings account, or HSA, is a type of savings account that lets you set aside money on a pre-tax basis to pay for qualified medical expenses.',
        ssQ: 'Collecting Social Security or Railroad Retirement?',
        mvQ: 'Moving to a different state within the next 12 months?',
        mvFollowLabel: 'Where are they moving to?',
        mvPlaceholder: 'New ZIP code (or state if unknown)',
        tvQ: 'Traveling out of state within the next 12 months?',
        countyLookupFailed: 'County lookup failed — re-enter the ZIP to retry.',
    },
    s3: {
        heading: 'Additional attendees',
        subPrefix: 'Spouse, family member, or friend joining the consultation. Each attendee who needs Medicare guidance requires ',
        subBold: 'their own',
        subSuffix: ' Scope of Appointment in step 4.',
        attendeeTitle: 'Attendee',
        guidanceTag: 'needs Medicare guidance',
        workedBefore: 'Have they worked with us before?',
        pillLookup: 'Yes, look up account',
        pillNewShort: 'No, new',
        guidanceQ: 'Will they need Medicare guidance?',
        pillOwnSoa: 'Yes, own SOA required',
        pillSittingIn: 'No, just sitting in',
        addLink: '＋ Add a family member or friend to the consultation',
        searchPlaceholder: 'Start typing their name…',
        noMatchesAttendee: 'No matches found. Check the spelling, or switch to “No, new”.',
        onFileNote: 'Account on file. Details carried over from CRM.',
        stillNeedsSoa: ' They still need their own SOA in step 4.',
        syncLink: '⇄ Same ZIP & coverage date as client',
        syncToastEmpty: "Enter the client's ZIP (and coverage date) first, then sync.",
        syncToast: (zip, cov) => `Copied from primary client: ${zip ? `ZIP ${zip}` : ''}${zip && cov ? ' · ' : ''}${cov ? `coverage by ${cov}` : ''}`,
        spouseAddS3: name => `＋ Add your spouse (${name}) to the consultation`,
        spouseAddedS3: name => `✓ Spouse ${name} added above.`,
        linkedInCrm: 'linked in CRM',
        spouseToast: name => `${name} added, account on file. Answer whether they need Medicare guidance.`,
    },
    s4: {
        heading: 'Verbal Scope of Appointment',
        introPrefix: 'Read each script ',
        introBold: 'verbatim',
        introSuffix: ' on the recorded line. One SOA per Medicare-eligible attendee. No one may consent for another person unless they are a Power of Attorney (POA) or other authorized representative. If asked, we do not need a copy of the POA, but if a carrier requests it, they must be able to provide one.',
        subClient: 'Capture while the client is on the line.',
        subAttendee: "This attendee must give their own consent. Bring them to the phone, or check the box below if they're unavailable.",
        scriptScopeLabel: 'Read verbatim · product scope',
        scriptScope: '“Before your appointment, Medicare requires us to confirm which products you agree to discuss. If you’re unsure about any of them, just let me know and your advisor can explain them during the consultation. So, during your consultation, would you like to discuss…”',
        notSurePrefix: "If they're not sure: ",
        notSureQuote: '“No problem, we\'ll include it and your advisor will walk you through it in plain terms.”',
        notSureSuffix: ' Not sure counts as ',
        notSureBold: 'in scope',
        notSureEnd: '. Every product needs an answer.',
        scriptConfirmLabel: 'Read verbatim · confirmation',
        // Contains the unmodified CMS model disclosure — never edit.
        confirmationPrefix: '“To confirm: you agree to discuss ',
        confirmationSuffix: ' at your appointment. Agreeing does not obligate you to enroll, does not affect your current or future Medicare benefits, and enrollment is never automatic. Your advisor is either employed or contracted by a Medicare plan and may be paid based on your enrollment in a plan. They do not work directly for the federal government.”',
        productsPlaceholder: '[answer each product above]',
        scriptClarifyLabel: 'After the disclosure · clarify if needed',
        clarify: '“That wording is required disclosure language from Medicare that we have to read. In practice, your advisor is a licensed, salaried employee of Doctor\'s Choice. Your advisor does not earn a commission and is not personally compensated by insurance companies.”',
        captureBtn: 'Client agreed, capture verbal SOA',
        capturedChip: time => `✓ Verbal SOA captured ${time}`,
        recordedNotePrefix: '✓ Verbal SOA recorded at ',
        recordedNoteSuffix: ' · valid 12 months for the products above.',
        undo: 'Undo',
        yes: 'Yes',
        no: 'No',
        notSure: 'Not sure',
        absentLabel: 'Attendee not present for verbal SOA',
        // DEVIATION (spec Decision #3): mockup said "The system flags the booking so
        // the advisor obtains…" — no flag exists, so state the advisor duty directly.
        absentWhy: "Check if this person can't come to the phone. The advisor must obtain their verbal SOA before any plan discussion with them.",
        // #605: a Power of Attorney / legal guardian may consent for a
        // beneficiary who can't (see introSuffix). Their name and relationship
        // fill the SOA's authorized-representative section, and their name signs
        // it in place of the attendee's.
        repLabel: 'Attendee has an authorized representative',
        // DEVIATION (#605): helper copy isn't in the approved mockup — it states
        // the rule already spelled out in COPY.s4.introSuffix.
        repWhy: 'Check if a Power of Attorney or other authorized representative is consenting on their behalf. Their name signs the SOA instead of the attendee’s.',
        repNameLabel: 'Representative’s full name',
        repRelationshipLabel: 'Relationship to beneficiary',
        repRelationshipTip: 'Examples include Power of Attorney (POA) or legal guardian.',
        toastRepIncomplete: 'Enter the representative’s full name and their relationship to the beneficiary.',
        // DEVIATION (spec Decision #3): dropped mockup sentence "This booking will be flagged."
        flagNote: name => `No SOA on file for ${name}. The advisor must capture their verbal SOA on a recorded line at the start of the consultation, before presenting any plan options to them. Until then, no plan discussion or recommendation may be directed to this attendee.`,
        scriptPresentLabel: 'Read to client · attendee must be present',
        mustBePresent: name => `“Since ${name} needs Medicare guidance, they will need to be present during the consultation when their options are discussed.”`,
        scriptPushbackLabel: 'If the client pushes back',
        pushback: name => `“If ${name} can't join the call, we have two easy options: we can send them a written recommendation by email to review on their own time, or we can set up a separate consultation just for them to go over their options.”`,
        toastUnanswered: n => `Every product needs an answer: ${n} left. “Not sure” counts as in scope.`,
        toastNoScope: 'At least one product must be in scope (Yes or Not sure) to hold the consultation.',
        absentChip: '⚠ Not present · no SOA',
    },
    rail: {
        methodHeading: 'Consultation method & time',
        // #727: Other-source clients.
        videoOpenStateNote: codes => `No video for ${codes.join(' / ')} residents without a paying source.`,
        advisorOpenStateNote: (codes, names) => `${codes.join(' / ')} resident without a paying source: only ${names} can be booked.`,
        noPreferenceAmong: names => `No preference (${names})`,
        referredBy: 'Referred by',
        referredByPending: 'Not answered',
        referredByClient: 'Current client',
        referredByEmployerPending: 'Employer: not entered',
        phone: 'Phone',
        video: 'Video',
        tzLabel: 'Timezone',
        tzHint: '(auto-set from ZIP)',
        advisorLabel: 'Specify advisor',
        noPreference: 'No preference',
        autoAssigned: ' · auto-assigned',
        emailOffer: 'None of the times work for the client?',
        emailOfferLink: 'Offer an email consultation instead',
        chooseMethod: 'Choose Phone or Video above to see open times.',
        emailOnlyNote: 'This client is eligible for an email consultation only.',
        emailModeNote: 'A licensed advisor will reach out by email within one business day.',
        switchBack: 'Switch back to a scheduled call',
        notesHeading: 'Notes for the advisor',
        notesPlaceholder: 'Current plans, medications, preferred doctors, anything to prepare…',
        pleaseWait: 'Please wait…',
        summaryHeading: 'Booking summary',
        client: 'Client',
        when: 'When',
        whenPending: 'Not selected yet',
        whenEmail: 'By email · within one business day',
        method: 'Method',
        advisor: 'Advisor',
        soaStatusHeading: 'Attendees & SOA status',
        chipSoa: time => `✓ SOA ${time}`,
        chipAdvisorCapture: '⚠ Advisor to capture',
        chipPending: 'Pending',
        chipSittingIn: 'Sitting in · no SOA needed',
        // DEVIATION (spec Decision #3): dropped mockup phrase "Booking will be flagged:".
        banner: n => `⚠️ SOA missing for ${n} attendee${n === 1 ? '' : 's'}. The advisor must capture their verbal SOA on the recorded line at the start of the consultation, before presenting any plan options to them.`,
        bookBtn: 'Book appointment',
        closingLabel: 'Read to client · closing',
        closingEmail: '“A licensed advisor will reach out to you by email within one business day. You\'ll also receive an email with a secure link to a short pre-consultation survey. Completing it ahead of time helps your advisor prepare for you.”',
        closingCall: (dateLabel, timeLabel, video) => `“You're all set for ${dateLabel} at ${timeLabel}. ${video ? 'Your Zoom link is included in your confirmation email.' : 'Your advisor will call you at that time.'} You'll also receive an email with a secure link to a short pre-consultation survey. Completing it before the call lets your advisor look up your doctors, prescriptions, and pharmacy ahead of time.”`,
        // #679 Phase 3 Task 3 (mockup panels I/J/K).
        methodSurvey: 'AEP survey',
        videoAepNote: 'Video is not offered for AEP reviews.',
        // #679 review fold-in minor (ii): the invented "30 to 45 minutes"
        // default-duration display was dropped — only this contractual
        // plan-review string remains, and the duration note now renders
        // nothing at all until schedule.reason === 'plan_review'.
        durationPlanReview: '30 minutes',
        earliestOpening: 'earliest opening 48 hours out',
        surveyOnlyHeading: 'AEP survey',
        surveyOnlyNotice: 'Only the AEP survey is available for this client. Consultations are not available, and questions come back by email reply.',
        surveyOnlyNote: 'Sends the survey to the client. Recommendation is due the later of October 15 and 3 business days after their completed survey.',
        surveyOnlyDue: date => `Recommendation due if completed today: ${date}`,
        sendSurveyBtn: 'Send AEP survey',
        // DEVIATION: not in the mockup/Global Constraints — needed so the rail
        // reads correctly once the "AEP survey" peer pill (panel I, not the
        // K-rail button) has been clicked while a call is still available.
        // Mirrors emailModeNote's shape for the survey method.
        surveySelectedNote: 'The AEP survey will be sent to the client by email.',
    },
    gates: {
        // #727
        referralType: 'Tell us who referred this client.',
        referralEmployer: "Enter the referring employer's name.",
        status: 'Select whether the client has worked with us before (step 1).',
        attendeeStatus: name => `Answer both questions for ${name}: worked with us before, and whether they need Medicare guidance.`,
        method: 'Select a consultation method (Phone or Video), or offer an email consultation.',
        reason: 'Please choose the consultation type (New to Medicare or AEP plan review).',
        time: 'Pick a date and time in the panel on the right before booking.',
        clientSc: n => `Answer all special circumstances questions for the client (${n} left).`,
        attendeeContact: name => `Email and phone are required for ${name} since they need Medicare guidance.`,
        attendeeReferral: name => `Select a referral source for ${name}.`,
        attendeeGroup: name => `Select the employer group for ${name}.`,
        attendeeGroupRelationship: name => `Select the group relationship for ${name}.`,
        attendeeSc: (name, n) => `Answer all special circumstances questions for ${name} (${n} left).`,
        soaPending: 'Capture (or flag) the verbal SOA for every attendee before booking.',
        lookup: 'Look up and select the returning client in step 1 before booking.',
        returningEmail: 'This client has no email on file. Add one to their profile first.',
        clientFields: "Complete the client's identity, contact and address fields (name, DOB, gender, email, phone, street, city, state).",
        referral: 'Select a referral source for the new client.',
        groupRelationship: "Select the client's group relationship (e.g. Employee, Spouse of employee).",
        zip: "Enter the client's ZIP code so we can find eligible advisors.",
        county: "Confirm the client's county for their ZIP before booking.",
        // #679 review Finding 1 (critical), fix part (b): a defensive
        // booking-time gate for schedule.aep && schedule.method === 'video'
        // (video is never offered under aep) — the suspenders alongside
        // MethodTimeCard.setAep's structural clear (part a, the belt). Wired
        // into logic.js's bookingGates (key 'video_aep') in fix round 2.
        videoAepBlocked: 'Video is not offered for AEP reviews. Choose phone or the AEP survey.',
        // #679 review fold-in minor #3(b) (phase review): the mirror gate —
        // survey only exists as a consult method under aep, so a stale
        // schedule.method === 'survey' surviving aep being turned off (the
        // belt is MethodTimeCard.setAep's symmetric clear, minor #3(a)) is
        // caught here too. Wired into logic.js's bookingGates (key
        // 'survey_non_aep'), beside the video_aep gate.
        surveyNonAepBlocked: 'The AEP survey is only available for AEP consultations.',
    },
}
